import { motion, Transition, Variants } from "motion/react"

// Slight overshoot past each target before settling, matching the bounce
// used across the other tool animations.
const OVERSHOOT_RATIO = 0.2
const bounce = (start: number, end: number) => [start, end + (end - start) * OVERSHOOT_RATIO, end]
const bounceTransition: Transition = { duration: 0.3, ease: "easeOut", times: [0, 0.3, 1] }
const fadeInTransition: Transition = { duration: 0.3, ease: "easeOut" }

// The three icon clusters just shrink and dim slightly as a unit - no need to
// morph each shape's own numbers, since the group's own scale/opacity carries it.
const iconGroupVariants: Variants = {
  rest: { scale: 1, opacity: 1 },
  hover: { scale: bounce(1, 0.9), opacity: bounce(1, 0.2), transition: bounceTransition },
}

// The notification card doesn't exist at rest - it rises into place as it
// fades in. Opacity skips the bounce (overshooting past 1 just clamps and
// does nothing visible); the rise still bounces.
const cardVariants: Variants = {
  rest: { y: 20, opacity: 0 },
  hover: { y: bounce(20, 0), opacity: 1, transition: { y: bounceTransition, opacity: fadeInTransition } },
}

export const DriveInTheMomentAnimation = ({ hover }: { hover?: boolean }) => (
  <motion.div
    initial="rest"
    animate={hover ? "hover" : "rest"}
    className="w-full aspect-[41/28] relative [&_svg]:absolute [&_svg]:w-full [&_svg]:h-auto overflow-hidden"
  >
    <svg width="408" height="260" viewBox="0 0 408 260" fill="none" xmlns="http://www.w3.org/2000/svg">
      <motion.g variants={iconGroupVariants}>
        <rect
          x="146.512"
          y="27.271"
          width="97.5091"
          height="97.5091"
          rx="15.4081"
          transform="rotate(45 146.512 27.271)"
          fill="#FFED48"
        />
        <path
          d="M146.512 73.8203C160.675 73.8203 172.158 85.3021 172.158 99.4658V100.389C172.158 100.941 172.606 101.389 173.158 101.389H185.603C186.155 101.389 186.603 101.836 186.603 102.389V105.673C186.603 106.225 186.155 106.673 185.603 106.673H107.422C106.87 106.673 106.422 106.225 106.422 105.673V102.389C106.422 101.836 106.87 101.389 107.422 101.389H119.866C120.418 101.389 120.866 100.941 120.866 100.389V99.4658C120.866 85.3022 132.348 73.8205 146.512 73.8203Z"
          fill="#222222"
        />
      </motion.g>

      <motion.g variants={iconGroupVariants}>
        <circle cx="270.722" cy="77.8344" r="53.5229" fill="#C62E1D" />
        <path
          d="M269.307 64.7238C270.089 65.5048 271.355 65.5048 272.136 64.7238L289.127 47.7332C289.908 46.9522 291.174 46.9522 291.955 47.7332L300.823 56.6017C301.605 57.3828 301.605 58.6491 300.823 59.4302L283.833 76.4207C283.052 77.2018 283.052 78.4681 283.833 79.2492L300.823 96.2398C301.605 97.0208 301.605 98.2871 300.823 99.0682L291.955 107.937C291.174 108.718 289.908 108.718 289.127 107.937L272.136 90.9461C271.355 90.1651 270.089 90.1651 269.307 90.9461L252.317 107.937C251.536 108.718 250.27 108.718 249.488 107.937L240.62 99.0682C239.839 98.2871 239.839 97.0208 240.62 96.2398L257.611 79.2492C258.392 78.4681 258.392 77.2018 257.611 76.4207L240.62 59.4302C239.839 58.6491 239.839 57.3828 240.62 56.6017L249.488 47.7332C250.27 46.9522 251.536 46.9522 252.317 47.7332L269.307 64.7238Z"
          fill="white"
        />
      </motion.g>

      <motion.g variants={iconGroupVariants}>
        <rect x="180.922" y="140.516" width="95.1739" height="95.1739" rx="15.4081" fill="#607CDE" />
        <path
          d="M234.007 158.633C234.627 158.633 235.386 158.663 236.284 158.721C237.205 158.75 238.086 158.839 238.924 158.988L238.923 158.989C242.468 159.552 245.45 160.768 247.848 162.656H247.847C250.264 164.537 252.072 166.912 253.265 169.774C254.453 172.627 255.043 175.768 255.043 179.191C255.043 182.643 254.453 185.798 253.265 188.65C252.072 191.513 250.264 193.888 247.847 195.769C245.447 197.658 242.463 198.877 238.914 199.438L238.907 199.439L238.836 198.944L238.906 199.439C238.08 199.558 237.196 199.644 236.257 199.703L236.258 199.704C235.353 199.762 234.602 199.793 234.007 199.793H218.777C218.501 199.793 218.277 200.017 218.277 200.293V220.787C218.277 221.615 217.606 222.287 216.777 222.287H209.684C208.855 222.287 208.184 221.616 208.184 220.787V160.133C208.184 159.304 208.855 158.633 209.684 158.633H234.007ZM218.777 168.204C218.501 168.204 218.277 168.428 218.277 168.704V189.678C218.277 189.954 218.501 190.178 218.777 190.178H233.658C234.222 190.178 234.846 190.149 235.531 190.092H235.533C236.236 190.036 236.892 189.938 237.504 189.799L237.833 189.717C239.458 189.286 240.753 188.557 241.741 187.544C242.819 186.409 243.59 185.113 244.06 183.65C244.538 182.16 244.775 180.674 244.775 179.191C244.775 177.709 244.538 176.237 244.061 174.777L244.06 174.775C243.59 173.312 242.819 172.03 241.741 170.925L241.737 170.921C240.683 169.813 239.281 169.043 237.504 168.626L237.486 168.622C236.881 168.457 236.23 168.346 235.533 168.29H235.531C234.846 168.233 234.222 168.204 233.658 168.204H218.777Z"
          fill="white"
          stroke="white"
        />
      </motion.g>

      <motion.g variants={cardVariants}>
        <rect x="98.8438" y="95.585" width="238.9" height="70.7423" rx="14" fill="white" />
        <rect x="112.094" y="108.335" width="50.1452" height="45.2417" rx="14" fill="#FFED48" />
        <rect opacity="0.2" x="175.316" y="115.966" width="142.804" height="12.6709" rx="6.33543" fill="#8E8E8E" />
        <rect opacity="0.2" x="175.316" y="133.407" width="124.817" height="12.6709" rx="6.33543" fill="#8E8E8E" />
        <circle cx="333.459" cy="97.285" r="11.162" fill="#C62E1D" />
      </motion.g>
    </svg>
  </motion.div>
)
